// context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { makeRequest } from "../makeRequest";
import { MY_ENDPOINTS } from "@raashidh/shared";
import MY_CONFIG from "@/constants";


// Types for your combined schema
export interface User {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  full_name: string;
  email_verified: boolean;
  tenant_id: string;
  app_id: string;
  two_factor_enabled: boolean;
  two_factor_verified?: boolean;
  biometrics_enabled: boolean;
  is_superadmin: boolean;
  role_names: string[];
  permissions: string[];
  roles: Array<{
    role_id: string;
    role_name: string;
    permissions: string[];
    is_system_role?: boolean;
    assigned_at?: string;
    is_active?: boolean;
  }>;
  profile_picture_url?: string;
  profile_status?: any;
  phone?: string;
  phone_verified?: boolean;
  phone_verified_at?: string;
  email_verified_at?: string;
  gender?: string;
  country?: string;
  preferences?: Record<string, any>;
  metadata?: Record<string, any>;
  last_login_at?: string;
  created_at: string;
  updated_at: string;
  status: string;
  tenant?: {
    id: string;
    code: string;
    name: string;
    timezone: string;
    status: string;
  };
  app?: {
    id: string;
    code: string;
    name: string;
    status: string;
  };
  biometrics?: Array<{
    biometric_id: string;
    biometric_type: string;
    device_name: string;
    device_type: string;
    is_primary: boolean;
    enrolled_at: string;
    last_used_at: string | null;
    use_count: number;
  }>;
  active_sessions?: Array<{
    session_id: string;
    device_name: string;
    device_type: string;
    device_os: string;
    device_browser: string;
    ip_address: string;
    last_used_at: string | null;
    access_token_expires_at: string;
    created_at: string;
  }>;
  stats?: {
    total_sessions: number;
    total_biometrics: number;
    total_roles: number;
  };
}

interface LoginResponse {
  success: boolean;
  user: User;
  tokens: {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
    session_id: string;
  };
}

interface OtpRequestResponse {
  requires_otp: boolean;
  otp_session_id: string;
  expires_in: number;
  message: string;
}

interface OtpLoginResponse {
  success: boolean;
  message: string;
  user: User;
  tokens: {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
    session_id: string;
  };
}

interface RegisterPayload {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  display_name?: string;
  gender?: string;
  phone?: string;
  country?: string;
  dob?: any;
}

interface RefreshTokenResponse {
  success: boolean;
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  session_id: string;
}

interface UpdateProfilePayload {
  first_name?: string;
  last_name?: string;
  display_name?: string;
  phone?: string;
  gender?: string;
  country?: string;
  profile_picture_url?: string;
  preferences?: Record<string, any>;
  metadata?: Record<string, any>;
}

interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
  confirm_new_password?: string;
}

interface TwoFactorSetupResponse {
  secret: string;
  uri: string;
}

interface BiometricEnrollPayload {
  biometric_type: string;
  biometric_key: string;
  device_name?: string;
  device_type?: string;
  is_primary?: boolean;
}

interface BiometricEnrollResponse {
  biometric_id: string;
}

interface Session {
  session_id: string;
  device_name: string;
  device_type: string;
  device_os: string;
  device_browser: string;
  ip_address: string;
  location: string | null;
  last_used_at: string | null;
  use_count: number;
  access_token_expires_at: string;
  refresh_token_expires_at: string;
  created_at: string;
  is_current?: boolean;
  status?: string;
}

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  sessionId: string | null;
  loading: boolean;
  twoFactorRequired: boolean;
  pendingTwoFactorSession: string | null;
  otpSessionId: string | null;
  otpExpiresIn: number | null;
  
  // Auth methods
  login: (email: string, password: string) => Promise<void>;
  loginWithOtp: (email: string, otpCode?: string, otpSessionId?: string) => Promise<void>;
  requestOtp: (email: string) => Promise<string>;
  register: (payload: RegisterPayload) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithGoogleRedirect: (accessToken: string, refreshToken?: string, sessionId?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshTokenMethod: () => Promise<string | null>;
  refreshUser: () => Promise<User | undefined>;
  
  // Profile management
  updateProfile: (payload: UpdateProfilePayload) => Promise<User>;
  changePassword: (payload: ChangePasswordPayload) => Promise<void>;
  deleteAccount: () => Promise<void>;
  
  setOtpSessionId: React.Dispatch<React.SetStateAction<string | null>>;
  
  // Two-factor authentication
  setupTwoFactor: () => Promise<TwoFactorSetupResponse>;
  verifyAndEnableTwoFactor: (code: string) => Promise<void>;
  verifyTwoFactor: (code: string) => Promise<{ access_token: string; session_id: string }>;
  enableTwoFactor: (code?: string) => Promise<TwoFactorSetupResponse | void>;
  disableTwoFactor: (code: string) => Promise<void>;
  
  // Biometrics
  enrollBiometric: (data: BiometricEnrollPayload) => Promise<BiometricEnrollResponse>;
  revokeBiometric: (biometricId: string) => Promise<void>;
  getBiometrics: () => Promise<any[]>;
  
  // Session management
  revokeSession: (sessionIdToRevoke: string) => Promise<void>;
  revokeAllSessions: () => Promise<void>;
  getSessions: () => Promise<Session[]>;
  
  // Email verification
  verifyEmail: (token: string) => Promise<void>;
  resendVerification: () => Promise<void>;
  
  // Password reset
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  
  // UI state
  isDarkMode: boolean;
  setIsDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [pendingTwoFactorSession, setPendingTwoFactorSession] = useState<string | null>(null);
  const [otpSessionId, setOtpSessionId] = useState<string | null>(null);
  const [otpExpiresIn, setOtpExpiresIn] = useState<number | null>(null);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem("dark_mode");
    return saved === "true";
  });

  // Save dark mode preference
  useEffect(() => {
    localStorage.setItem("dark_mode", isDarkMode.toString());
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDarkMode]);

  // Fetch user profile using access token
const refreshUser = useCallback(async () => {
  // Always get the latest token from localStorage first
  const token = accessToken || localStorage.getItem(MY_CONFIG.accesstokenName);
  
  if (!token) {
    console.log("No token available for refreshUser");
    setUser(null);
    setLoading(false);
    return undefined;
  }

  try {
    console.log("Fetching user with token:", token.substring(0, 20) + "...");
    
    const response = await makeRequest<{ success: boolean; error?: any; user: User }>(
      MY_ENDPOINTS.AUTH_ENDPOINTS.GET_USER.path,
      {
        method: MY_ENDPOINTS.AUTH_ENDPOINTS.GET_USER.method,
        auth: true,
      }
    );

    console.log("RefreshUser response:", response);

    if (!response?.success) {
      throw new Error(response?.error || "Failed to fetch user");
    }

    // Set user data
    setUser(response.user);
    
    // Check if 2FA is required but not verified
    if (response.user.two_factor_enabled && !response.user.two_factor_verified) {
      setTwoFactorRequired(true);
    } else {
      setTwoFactorRequired(false);
    }
    
    return response.user;
  } catch (err) {
    console.error("User fetch failed:", err);
    // Don't logout on refresh failure, let refreshToken handle it
    // But clear user data
    setUser(null);
    return undefined;
  } finally {
    setLoading(false);
  }
}, [accessToken]);


// Initialize auth from localStorage
useEffect(() => {
  const initAuth = async () => {
    console.log("🔍 Initializing Auth...");
    const savedToken = localStorage.getItem(MY_CONFIG.accesstokenName);
    const savedRefreshToken = localStorage.getItem(MY_CONFIG.refreshtokenName);
    const savedSessionId = localStorage.getItem("session_id");

     console.log("💾 Token from localStorage:", savedToken ? "Found" : "Not found");
    console.log("📋 All localStorage keys:", Object.keys(localStorage));
    

    console.log("Initializing auth - Token found:", !!savedToken);

    if (savedToken) {
      console.log("Token preview:", savedToken.substring(0, 20) + "...");
      setAccessToken(savedToken);
      if (savedRefreshToken) setRefreshToken(savedRefreshToken);
      if (savedSessionId) setSessionId(savedSessionId);
      
      // Wait for state to update? No, refreshUser will use localStorage
      await refreshUser();
    } else {
      setLoading(false);
    }
  };

  initAuth();
}, [refreshUser]);
  // Login
  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      const response = await makeRequest<LoginResponse>(
        MY_ENDPOINTS.AUTH_ENDPOINTS.LOGIN.path,
        {
          method: MY_ENDPOINTS.AUTH_ENDPOINTS.LOGIN.method,
          body: { email, password },
          auth: false,
        }
      );

      const { access_token, refresh_token, session_id } = response.tokens;
      
      localStorage.setItem(MY_CONFIG.accesstokenName, access_token);
      localStorage.setItem(MY_CONFIG.refreshtokenName, refresh_token);
      localStorage.setItem("session_id", session_id);
      
      setAccessToken(access_token);
      setRefreshToken(refresh_token);
      setSessionId(session_id);
      setUser(response.user);
      
      // Check if 2FA is required
      if (response.user.two_factor_enabled) {
        setTwoFactorRequired(true);
        setPendingTwoFactorSession(session_id);
      }
    } catch (err) {
      console.error("Login failed", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };


  // Request OTP for login
  const requestOtp = async (email: string): Promise<string> => {
    try {
      setLoading(true);
      const response = await makeRequest<OtpRequestResponse>(
        `${MY_ENDPOINTS.AUTH_ENDPOINTS.LOGIN_OTP?.path || "/auth/login/otp/request"}`,
        {
          method: "POST",
          body: { email },
          auth: false,
        }
      );

      if (response.requires_otp) {
        setOtpSessionId(response.otp_session_id);
        setOtpExpiresIn(response.expires_in);
        return response.otp_session_id;
      }
      
      throw new Error("Failed to request OTP");
    } catch (err) {
      console.error("Request OTP failed", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Login with OTP
  const loginWithOtp = async (email: string, otpCode?: string, otpSessionIdParam?: string) => {
    try {
      setLoading(true);
      
      // If no OTP code provided, just request OTP
      if (!otpCode) {
        const sessionId = await requestOtp(email);
        return;
      }
      
      // Verify OTP and login
      const response = await makeRequest<OtpLoginResponse>(
        MY_ENDPOINTS.AUTH_ENDPOINTS.LOGIN_OTP?.path || "/auth/login/otp",
        {
          method: "POST",
          body: { 
            email, 
            otp_code: otpCode,
            otp_session_id: otpSessionIdParam || otpSessionId
          },
          auth: false,
        }
      );

      if (response.success) {
        const { access_token, refresh_token, session_id } = response.tokens;
        
        localStorage.setItem(MY_CONFIG.accesstokenName, access_token);
        localStorage.setItem(MY_CONFIG.refreshtokenName, refresh_token);
        localStorage.setItem("session_id", session_id);
        
        setAccessToken(access_token);
        setRefreshToken(refresh_token);
        setSessionId(session_id);
        setUser(response.user);
        setOtpSessionId(null);
        setOtpExpiresIn(null);
        
        if (response.user.two_factor_enabled) {
          setTwoFactorRequired(true);
          setPendingTwoFactorSession(session_id);
        }
      } else {
        throw new Error(response.message || "OTP verification failed");
      }
    } catch (err) {
      console.error("OTP login failed", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };





  // Register
  const register = async (payload: RegisterPayload) => {
    try {
      setLoading(true);
      const response = await makeRequest<LoginResponse>(
        MY_ENDPOINTS.AUTH_ENDPOINTS.REGISTER.path,
        {
          method: MY_ENDPOINTS.AUTH_ENDPOINTS.REGISTER.method,
          body: payload,
          auth: false,
        }
      );

      const { access_token, refresh_token, session_id } = response.tokens;

      localStorage.setItem(MY_CONFIG.accesstokenName, access_token);
      localStorage.setItem(MY_CONFIG.refreshtokenName, refresh_token);
      localStorage.setItem("session_id", session_id);
      
      setAccessToken(access_token);
      setRefreshToken(refresh_token);
      setSessionId(session_id);
      setUser(response.user);
    } catch (err) {
      console.error("Register failed", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update Profile
  const updateProfile = async (payload: UpdateProfilePayload): Promise<User> => {
    try {
      const response = await makeRequest<{ success: boolean; user: User }>(
        MY_ENDPOINTS.AUTH_ENDPOINTS.UPDATE_PROFILE?.path || "/api/auth/me",
        {
          method: "PUT",
          body: payload,
          auth: true,
        }
      );

      if (!response.success) {
        throw new Error("Failed to update profile");
      }

      setUser(response.user);
      return response.user;
    } catch (err) {
      console.error("Update profile failed:", err);
      throw err;
    }
  };

  // Change Password
  const changePassword = async (payload: ChangePasswordPayload): Promise<void> => {
    try {
      await makeRequest(MY_ENDPOINTS.AUTH_ENDPOINTS.FORGOT_PASSWORD.path, {
        method: MY_ENDPOINTS.AUTH_ENDPOINTS.FORGOT_PASSWORD.method,
        body: payload,
        auth: true,
      });
    } catch (err) {
      console.error("Change password failed:", err);
      throw err;
    }
  };

  // Delete Account
  const deleteAccount = async (): Promise<void> => {
    try {
      await makeRequest(MY_ENDPOINTS.AUTH_ENDPOINTS.DELETE_ACCOUNT.path, {
        method: "DELETE",
        auth: true,
      });
      await logout();
    } catch (err) {
      console.error("Delete account failed:", err);
      throw err;
    }
  };

  // Setup Two-Factor
  const setupTwoFactor = async (): Promise<TwoFactorSetupResponse> => {
    try {
      const response = await makeRequest<TwoFactorSetupResponse>(
        MY_ENDPOINTS.AUTH_ENDPOINTS.ENABLE_2FA.path,
        {
          method: "POST",
          body: {},
          auth: true,
        }
      );
      return response;
    } catch (err) {
      console.error("Setup 2FA failed", err);
      throw err;
    }
  };

  // Verify and Enable Two-Factor
  const verifyAndEnableTwoFactor = async (code: string): Promise<void> => {
    try {
      await makeRequest(MY_ENDPOINTS.AUTH_ENDPOINTS.VERIFY_2FA.path, {
        method: "POST",
        body: { code },
        auth: true,
      });
      await refreshUser();
    } catch (err) {
      console.error("Verify and enable 2FA failed", err);
      throw err;
    }
  };

  // Verify Two-Factor Authentication
  const verifyTwoFactor = async (code: string) => {
    try {
      setLoading(true);
      const response = await makeRequest<{ success: boolean; access_token: string; session_id: string }>(
        MY_ENDPOINTS.AUTH_ENDPOINTS.VERIFY_2FA.path,
        {
          method: "POST",
          body: { code },
          auth: true,
        }
      );

      const { access_token, session_id } = response;
      
      localStorage.setItem(MY_CONFIG.accesstokenName, access_token);
      localStorage.setItem("session_id", session_id);
      
      setAccessToken(access_token);
      setSessionId(session_id);
      setTwoFactorRequired(false);
      setPendingTwoFactorSession(null);
      
      // Refresh user to update 2FA status
      await refreshUser();
      
      return { access_token, session_id };
    } catch (err) {
      console.error("2FA verification failed", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Enable Two-Factor (wrapper for backward compatibility)
  const enableTwoFactor = async (code?: string): Promise<TwoFactorSetupResponse | void> => {
    if (code) {
      return verifyAndEnableTwoFactor(code);
    }
    return setupTwoFactor();
  };

  // Disable Two-Factor Authentication
  const disableTwoFactor = async (code: string) => {
    try {
      await makeRequest("/api/auth/2fa/disable", {
        method: "POST",
        body: { code },
        auth: true,
      });
      await refreshUser();
    } catch (err) {
      console.error("Disable 2FA failed", err);
      throw err;
    }
  };

  // Enroll Biometric
  const enrollBiometric = async (data: BiometricEnrollPayload): Promise<BiometricEnrollResponse> => {
    try {
      const response = await makeRequest<BiometricEnrollResponse>(
        MY_ENDPOINTS.AUTH_ENDPOINTS.ENROLL_BIOMETRIC.path,
        {
          method: "POST",
          body: data,
          auth: true,
        }
      );
      await refreshUser();
      return response;
    } catch (err) {
      console.error("Enroll biometric failed", err);
      throw err;
    }
  };

  // Revoke Biometric
  const revokeBiometric = async (biometricId: string) => {
    try {
      await makeRequest(`${MY_ENDPOINTS.AUTH_ENDPOINTS.REVOKE_BIOMETRIC.path}/${biometricId}`, {
        method: "DELETE",
        auth: true,
      });
      await refreshUser();
    } catch (err) {
      console.error("Revoke biometric failed", err);
      throw err;
    }
  };

  // Get Biometrics
  const getBiometrics = async () => {
    try {
      const response = await makeRequest<{ success: boolean; biometrics: any[] }>(
       MY_ENDPOINTS.AUTH_ENDPOINTS.GET_BIOMETRICS.path,
        {
          method: "GET",
          auth: true,
        }
      );
      return response.biometrics;
    } catch (err) {
      console.error("Get biometrics failed:", err);
      throw err;
    }
  };

  // Revoke Session
  const revokeSession = async (sessionIdToRevoke: string) => {
    try {
      await makeRequest(`${MY_ENDPOINTS.AUTH_ENDPOINTS.REVOKE_SESSION.path}/${sessionIdToRevoke}`, {
        method: "DELETE",
        auth: true,
      });
    } catch (err) {
      console.error("Revoke session failed", err);
      throw err;
    }
  };

  // Revoke All Sessions
  const revokeAllSessions = async () => {
    try {
      await makeRequest(MY_ENDPOINTS.AUTH_ENDPOINTS.REVOKE_ALL_SESSIONS.path, {
        method: "DELETE",
        auth: true,
      });
    } catch (err) {
      console.error("Revoke all sessions failed", err);
      throw err;
    }
  };

  // Get Sessions
  const getSessions = async (): Promise<Session[]> => {
    try {
      const response = await makeRequest<{ success: boolean; sessions: Session[] }>(
        MY_ENDPOINTS.AUTH_ENDPOINTS.GET_SESSIONS.path,
        {
          method: "GET",
          auth: true,
        }
      );
      return response.sessions || [];
    } catch (err) {
      console.error("Get sessions failed:", err);
      throw err;
    }
  };

  // Verify Email
  const verifyEmail = async (token: string) => {
    try {
      await makeRequest(MY_ENDPOINTS.AUTH_ENDPOINTS.VERIFY_EMAIL.path, {
        method: "POST",
        body: { token },
        auth: false,
      });
      await refreshUser();
    } catch (err) {
      console.error("Verify email failed:", err);
      throw err;
    }
  };

  // Resend Verification
  const resendVerification = async () => {
    try {
      await makeRequest(MY_ENDPOINTS.AUTH_ENDPOINTS.RESEND_VERIFICATION.path, {
        method: "POST",
        auth: true,
      });
    } catch (err) {
      console.error("Resend verification failed:", err);
      throw err;
    }
  };

  // Forgot Password
  const forgotPassword = async (email: string) => {
    try {
      await makeRequest(MY_ENDPOINTS.AUTH_ENDPOINTS.FORGOT_PASSWORD.path, {
        method: "POST",
        body: { email },
        auth: false,
      });
    } catch (err) {
      console.error("Forgot password failed:", err);
      throw err;
    }
  };

  // Reset Password
  const resetPassword = async (token: string, newPassword: string) => {
    try {
      await makeRequest(MY_ENDPOINTS.AUTH_ENDPOINTS.RESET_PASSWORD.path, {
        method: "POST",
        body: { token, new_password: newPassword },
        auth: false,
      });
    } catch (err) {
      console.error("Reset password failed:", err);
      throw err;
    }
  };

  // Refresh token
  const refreshTokenMethod = async (): Promise<string | null> => {
    const currentRefreshToken = refreshToken || localStorage.getItem(MY_CONFIG.refreshtokenName);
    
    if (!currentRefreshToken) return null;

    try {
      const response = await makeRequest<RefreshTokenResponse>(
        MY_ENDPOINTS.AUTH_ENDPOINTS.REFRESH_TOKEN.path,
        {
          method: "POST",
          body: { refresh_token: currentRefreshToken },
          auth: false,
        }
      );

      const { access_token, refresh_token, session_id } = response;
      
      localStorage.setItem(MY_CONFIG.accesstokenName, access_token);
      localStorage.setItem(MY_CONFIG.refreshtokenName, refresh_token);
      localStorage.setItem("session_id", session_id);
      
      setAccessToken(access_token);
      setRefreshToken(refresh_token);
      setSessionId(session_id);
      
      return access_token;
    } catch (err) {
      console.error("Token refresh failed:", err);
      await logout();
      return null;
    }
  };

  // Google login - redirect
  const loginWithGoogle = async () => {
    try {
      window.location.href = `${MY_CONFIG.API_URL}${MY_ENDPOINTS.AUTH_ENDPOINTS.GOOGLE_LOGIN.path}`;
    } catch (err) {
      console.error("Google login failed", err);
    }
  };

  // Handle Google redirect with tokens
  const loginWithGoogleRedirect = async (token: string, refreshTokenParam?: string, sessionIdParam?: string) => {
    try {
      setLoading(true);
      
      localStorage.setItem(MY_CONFIG.accesstokenName, token);
      setAccessToken(token);
      
      if (refreshTokenParam) {
        localStorage.setItem(MY_CONFIG.refreshtokenName, refreshTokenParam);
        setRefreshToken(refreshTokenParam);
      }
      
      if (sessionIdParam) {
        localStorage.setItem("session_id", sessionIdParam);
        setSessionId(sessionIdParam);
      }
      
      await refreshUser();
    } catch (err) {
      console.error("Google callback handling failed:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const logout = useCallback(async () => {
    try {
      // Attempt to revoke session on server
      if (accessToken) {
        await makeRequest(MY_ENDPOINTS.AUTH_ENDPOINTS.LOGOUT.path, {
          method: "POST",
          auth: true,
        }).catch(console.error);
      }
    } catch (err) {
      console.error("Logout API call failed:", err);
    } finally {
      setUser(null);
      setAccessToken(null);
      setRefreshToken(null);
      setSessionId(null);
      setTwoFactorRequired(false);
      setPendingTwoFactorSession(null);
      setOtpSessionId(null);
      setOtpExpiresIn(null);
      localStorage.removeItem(MY_CONFIG.accesstokenName);
      localStorage.removeItem(MY_CONFIG.refreshtokenName);
      localStorage.removeItem("session_id");
    }
  }, [accessToken]);

  const value: AuthContextType = {
    user,
    accessToken,
    refreshToken,
    sessionId,
    loading,
    twoFactorRequired,
    pendingTwoFactorSession,
    otpSessionId,
    setOtpSessionId,
    otpExpiresIn,
    login,
    loginWithOtp,
    requestOtp,
    register,
    loginWithGoogle,
    loginWithGoogleRedirect,
    logout,
    refreshTokenMethod,
    refreshUser,
    updateProfile,
    changePassword,
    deleteAccount,
    setupTwoFactor,
    verifyAndEnableTwoFactor,
    verifyTwoFactor,
    enableTwoFactor,
    disableTwoFactor,
    enrollBiometric,
    revokeBiometric,
    getBiometrics,
    revokeSession,
    revokeAllSessions,
    getSessions,
    verifyEmail,
    resendVerification,
    forgotPassword,
    resetPassword,
    isDarkMode,
    setIsDarkMode,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};